/**
 * tokenBalanceEvents.ts
 *
 * Lightweight pub/sub for token-balance changes. Producers (the admin
 * grant modal, the tipping flow, future token-purchase webhooks-via-poll)
 * dispatch the event; consumers (the navbar balance badge in AppLayout,
 * TokensPage, TipButton) listen and refetch.
 *
 * Why a CustomEvent instead of context/store?
 *   The balance is read in three independent places that don't share a
 *   provider tree (header, page, button). A DOM-level event lets each
 *   component subscribe in isolation without forcing a top-level store
 *   refactor. It also means tabs of the same SPA stay consistent through
 *   the storage event mirror below — without us having to wire up a
 *   broadcast channel.
 *
 * Detail shape:
 *   userId      The user whose balance changed. Listeners that care only
 *               about a specific user (e.g., the navbar showing the
 *               current viewer's balance) compare against this value.
 *   newBalance  The post-change balance, when known. May be null if the
 *               source doesn't have an authoritative number to publish
 *               (in which case listeners should refetch from the API).
 *   source      A short string for debugging — which subsystem fired it.
 */

export const TOKEN_BALANCE_CHANGED_EVENT = 'vellum:token-balance-changed';

export interface TokenBalanceChangedDetail {
  userId: number;
  newBalance: number | null;
  source: 'admin-grant' | 'tip-sent' | 'tip-received' | 'purchase' | 'other';
}

/**
 * Emit a token-balance-changed event. Safe to call in any environment —
 * a no-op when `window` is unavailable (SSR, tests, etc).
 */
export function emitTokenBalanceChanged(detail: TokenBalanceChangedDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOKEN_BALANCE_CHANGED_EVENT, { detail }));
}

/**
 * Subscribe to token-balance changes. Returns a cleanup function suitable
 * for use as the return value of a useEffect. The handler only fires
 * when the event matches the current viewer's userId — passing
 * `viewerUserId: null` disables the subscription (used while the viewer
 * is logged out).
 */
export function subscribeToTokenBalanceChanges(
  viewerUserId: number | null,
  handler: (detail: TokenBalanceChangedDetail) => void,
): () => void {
  if (typeof window === 'undefined' || viewerUserId == null) {
    return () => undefined;
  }
  const onEvent = (e: Event) => {
    const detail = (e as CustomEvent<TokenBalanceChangedDetail>).detail;
    if (!detail || detail.userId !== viewerUserId) return;
    handler(detail);
  };
  window.addEventListener(TOKEN_BALANCE_CHANGED_EVENT, onEvent);
  return () => window.removeEventListener(TOKEN_BALANCE_CHANGED_EVENT, onEvent);
}
